import {
  createTranslatorFrom,
  isLocale,
  resolveLocale,
  type Locale,
  type Translations,
  type Translator,
} from '@webgame/i18n';

const STORAGE_KEY = 'webgame.locale';

function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Preference simply won't survive a reload.
  }
}

/**
 * Each language's catalog in a chunk of its own (NET-CLIENT-CACHE C2): a page downloads its
 * player's alone, and another only when the player switches to it.
 */
function loadCatalog(locale: Locale): Promise<Translations> {
  switch (locale) {
    case 'en':
      return import('@webgame/i18n/catalogs/en').then((module) => module.en);
    case 'es':
      return import('@webgame/i18n/catalogs/es').then((module) => module.es);
  }
}

/**
 * Browser translator: explicit choice (remembered per browser) first, then the browser's
 * language preferences, then English. Keeps <html lang> in sync for accessibility. Resolves once
 * its catalog is here.
 */
export async function createClientTranslator(): Promise<Translator> {
  const initial = readStoredLocale() ?? resolveLocale(navigator.languages);
  const translator = createTranslatorFrom(
    { [initial]: await loadCatalog(initial) },
    initial,
    loadCatalog,
  );
  document.documentElement.lang = initial;
  translator.subscribe((locale) => {
    storeLocale(locale);
    document.documentElement.lang = locale;
  });
  return translator;
}
